import {
  useCreateVendor,
  useDeleteVendor,
  useListVendors,
  useUpdateVendor,
  type VendorIn,
  type VendorListOut,
  type VendorOut,
} from '@myrentalmate/api-client';

import { Chip, Stack } from '@mui/material';

import {
  EMAIL_FIELD,
  optional,
  PHONE_FIELD,
  phoneColumn,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { StatRow, StatTile } from '../../components/Stats';
import { listResult } from '../../lib/apiData';
import { formatDate } from '../../lib/format';
import { formatPhone } from '../../lib/contact';
import { useCan } from '../../lib/permissions';
import { useDashboardSummary } from '../../lib/summary';
import { RecordExtras } from '../records/RecordExtras';

const FIELDS: Field[] = [
  { name: 'name', label: 'Name', required: true },
  { name: 'trade', label: 'Trade', helperText: 'Plumbing, landscaping, and so on' },
  { name: 'contact_name', label: 'Contact Name' },
  EMAIL_FIELD,
  PHONE_FIELD,
  { name: 'insurance_expires_on', label: 'Insurance Expires', type: 'date' },
  { name: 'receives_1099', label: 'Receives a 1099', type: 'checkbox' },
  {
    name: 'w9_received_on',
    label: 'W-9 Received',
    type: 'date',
    helperText: "Put the form itself in this vendor's files",
  },
  { name: 'notes', label: 'Notes', type: 'multiline' },
];

const INSURANCE: Record<
  VendorListOut['insurance_status'],
  { label: string; color: 'default' | 'success' | 'warning' | 'error' }
> = {
  not_recorded: { label: 'Not recorded', color: 'default' },
  current: { label: 'Current', color: 'success' },
  expiring: { label: 'Expiring soon', color: 'warning' },
  expired: { label: 'Expired', color: 'error' },
};

const COLUMNS: Column<VendorListOut>[] = [
  { label: 'Name', render: (v) => v.name },
  { label: 'Trade', render: (v) => v.trade ?? '–' },
  { label: 'Contact', render: (v) => v.contact_name ?? '–' },
  phoneColumn(),
  {
    label: 'Insurance',
    render: (v) => (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Chip
          size="small"
          label={INSURANCE[v.insurance_status].label}
          color={INSURANCE[v.insurance_status].color}
        />
        <span>{v.insurance_expires_on ? formatDate(v.insurance_expires_on) : ''}</span>
      </Stack>
    ),
  },
  { label: '1099', render: (v) => (v.receives_1099 ? 'Yes' : 'No') },
  {
    label: 'W-9',
    render: (v) =>
      v.w9_received_on ? (
        formatDate(v.w9_received_on)
      ) : v.receives_1099 ? (
        <Chip size="small" color="warning" label="Missing" />
      ) : (
        '–'
      ),
  },
];

function toBody(values: FormValues): VendorIn {
  return {
    name: required(values, 'name'),
    trade: optional(values, 'trade'),
    contact_name: optional(values, 'contact_name'),
    email: optional(values, 'email'),
    phone: optional(values, 'phone'),
    insurance_expires_on: optional(values, 'insurance_expires_on'),
    w9_received_on: optional(values, 'w9_received_on'),
    receives_1099: Boolean(values.receives_1099),
    notes: optional(values, 'notes'),
  };
}

function toValues(v: VendorOut): FormValues {
  return {
    name: v.name,
    trade: v.trade ?? '',
    contact_name: v.contact_name ?? '',
    email: v.email ?? '',
    phone: formatPhone(v.phone),
    insurance_expires_on: v.insurance_expires_on ?? '',
    w9_received_on: v.w9_received_on ?? '',
    receives_1099: v.receives_1099,
    notes: v.notes ?? '',
  };
}

function useRows({ q, page, pageSize }: ListParams) {
  return listResult(useListVendors({ q, page, page_size: pageSize }));
}

export function VendorsPage() {
  const canManage = useCan('vendors.manage');
  const create = useCreateVendor();
  const update = useUpdateVendor();
  const remove = useDeleteVendor();
  const summary = useDashboardSummary();
  return (
    <RecordPage
      title="Vendors"
      description="Who you call for repairs and upkeep, and whether their insurance is current."
      summary={
        <StatRow>
          <StatTile label="Vendors" value={summary?.vendors} />
          <StatTile label="Insurance Expired" value={summary?.vendors_insurance_expired} />
          <StatTile label="Expiring in 30 Days" value={summary?.vendors_insurance_expiring} />
          <StatTile label="Receive a 1099" value={summary?.vendors_receiving_1099} />
        </StatRow>
      }
      singular="vendor"
      fields={FIELDS}
      columns={COLUMNS}
      canManage={canManage}
      useRows={useRows}
      toValues={toValues}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) => update.mutateAsync({ recordId, data: toBody(values) })}
      remove={(recordId) => remove.mutateAsync({ recordId })}
      renderExtras={(row) => (
        <RecordExtras entityType="vendor" entityId={row.id} canManage={canManage} />
      )}
    />
  );
}
